import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import BackButton from '../../components/BackButton';
import {
  addImagesToGallery,
  fetchGalleryById,
  removeImageFromGallery,
  reorderGalleryImages,
  updateGallery,
} from '../../controllers/allGalleryController';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';
import { fetchAllMeetings, fetchMeetingById } from '../../controllers/meetingsController';
import { fetchEventById, searchClubEvents } from '../../controllers/clubEventsController';
import { fetchNewsById } from '../../controllers/newsController';
import {
  GALLERY_ENTITY_TYPE,
  GALLERY_ENTITY_TYPE_OPTIONS,
  galleryEntityTypeLabel,
} from '../../constants/galleryEntityTypes';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const cardSx = {
  border: '1px solid #e7e7e7',
  boxShadow: 'none',
  borderRadius: '8px',
  overflow: 'hidden',
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
  },
  '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
};

const imagesPanelSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f5f5f5',
  borderRadius: '8px',
  overflow: 'hidden',
  p: 2.5,
};

const GalleriesDetail = () => {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [altText, setAltText] = useState('');
  const [images, setImages] = useState([]);

  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    relatedEntityType: GALLERY_ENTITY_TYPE.Standalone,
    relatedEntityId: null,
    relatedEntityStringId: null,
    showOnDetailPage: true,
    showInGalleryPool: true,
  });

  const [entityQuery, setEntityQuery] = useState('');
  const [entityLoading, setEntityLoading] = useState(false);
  const [entityOptions, setEntityOptions] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [linkedEntityName, setLinkedEntityName] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [viewerZoom, setViewerZoom] = useState(1);

  const loadGallery = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await fetchGalleryById(id);
      setGallery(detail);
      setEditForm({
        title: detail?.title || '',
        description: detail?.description || '',
        relatedEntityType: Number(detail?.relatedEntityType ?? GALLERY_ENTITY_TYPE.Standalone),
        relatedEntityId: detail?.relatedEntityId || null,
        relatedEntityStringId: detail?.relatedEntityStringId || null,
        showOnDetailPage: detail?.showOnDetailPage !== false,
        showInGalleryPool: detail?.showInGalleryPool !== false,
      });
      const arr = Array.isArray(detail?.images) ? [...detail.images] : [];
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setImages(arr);
      setEditError('');
      setEntityQuery('');
      setEntityOptions([]);
      setLinkedEntityName('');

      if (detail?.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting && detail?.relatedEntityId) {
        try {
          const meeting = await fetchMeetingById(detail.relatedEntityId);
          const label = meeting?.title || `ID: ${detail.relatedEntityId}`;
          setLinkedEntityName(label);
          setSelectedEntity({ id: detail.relatedEntityId, label });
        } catch {
          setSelectedEntity({ id: detail.relatedEntityId, label: `ID: ${detail.relatedEntityId}` });
          setLinkedEntityName(`ID: ${detail.relatedEntityId}`);
        }
      } else if (detail?.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent && detail?.relatedEntityId) {
        try {
          const event = await fetchEventById(detail.relatedEntityId);
          const label = event?.title || `ID: ${detail.relatedEntityId}`;
          setLinkedEntityName(label);
          setSelectedEntity({ id: detail.relatedEntityId, label });
        } catch {
          setSelectedEntity({ id: detail.relatedEntityId, label: `ID: ${detail.relatedEntityId}` });
          setLinkedEntityName(`ID: ${detail.relatedEntityId}`);
        }
      } else if (detail?.relatedEntityType === GALLERY_ENTITY_TYPE.News && detail?.relatedEntityStringId) {
        try {
          const news = await fetchNewsById(detail.relatedEntityStringId);
          const label = news?.title || `ID: ${detail.relatedEntityStringId}`;
          setLinkedEntityName(label);
          setSelectedEntity({ id: detail.relatedEntityStringId, label });
        } catch {
          setSelectedEntity({ id: detail.relatedEntityStringId, label: `ID: ${detail.relatedEntityStringId}` });
          setLinkedEntityName(`ID: ${detail.relatedEntityStringId}`);
        }
      } else {
        setSelectedEntity(null);
      }
    } catch (e) {
      toast.error(e?.message || 'Galeri detayı alınamadı');
      setGallery(null);
      setImages([]);
      setSelectedEntity(null);
      setLinkedEntityName('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isEntityRequired =
    editForm.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting
    || editForm.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent;
  const linkedEntityPath = editForm.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting
    ? `/events/weekly-meetings/${editForm.relatedEntityId}`
    : editForm.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent
      ? `/events/${editForm.relatedEntityId}`
      : editForm.relatedEntityType === GALLERY_ENTITY_TYPE.News
        ? `/news/${editForm.relatedEntityStringId}`
        : null;

  useEffect(() => {
    if (!isEntityRequired) {
      setEntityOptions([]);
      return;
    }
    if (!entityQuery || entityQuery.trim().length < 2) {
      setEntityOptions([]);
      return;
    }

    let active = true;
    setEntityLoading(true);
    (async () => {
      try {
        if (editForm.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting) {
          const { data } = await fetchAllMeetings({
            pageNumber: 1,
            pageSize: 20,
            searchTerm: entityQuery.trim(),
          });
          if (!active) return;
          setEntityOptions((data || []).map((m) => ({ id: m.id, label: m.title || '-' })));
        } else if (editForm.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent) {
          const { data } = await searchClubEvents({
            keyword: entityQuery.trim(),
            pageNumber: 1,
            pageSize: 20,
          });
          if (!active) return;
          setEntityOptions((data || []).map((e) => ({ id: e.id, label: e.title || '-' })));
        }
      } catch (e) {
        if (!active) return;
        setEntityOptions([]);
      } finally {
        if (active) setEntityLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isEntityRequired, entityQuery, editForm.relatedEntityType]);

  const handleSaveEdit = async () => {
    setEditError('');
    if (!gallery?.id) return;
    setEditSaving(true);
    try {
      await updateGallery({
        id: gallery.id,
        title: editForm.title?.trim() || null,
        showOnDetailPage: Boolean(editForm.showOnDetailPage),
        showInGalleryPool: Boolean(editForm.showInGalleryPool),
      });
      toast.success('Galeri güncellendi');
      await loadGallery();
    } catch (e) {
      setEditError(e?.message || 'Galeri kaydedilemedi');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddImages = async (fileList) => {
    if (!id) return;
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    if (images.length + picked.length > 50) {
      toast.error('Bir galeri en fazla 50 görsel içerebilir.');
      return;
    }
    setUploading(true);
    try {
      await addImagesToGallery(id, picked, altText?.trim() || null);
      setAltText('');
      await loadGallery();
      toast.success('Görseller eklendi');
    } catch (e) {
      toast.error(e?.message || 'Görseller eklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageId) => {
    if (!id) return;
    try {
      await removeImageFromGallery(id, imageId);
      await loadGallery();
      toast.success('Görsel kaldırıldı');
    } catch (e) {
      toast.error(e?.message || 'Görsel kaldırılamadı');
    }
  };

  const handleDragEnd = async (event) => {
    if (uploading || editSaving) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(images, oldIndex, newIndex);
    setImages(newOrder);
    try {
      await reorderGalleryImages(id, newOrder.map((x) => x.id));
      await loadGallery();
    } catch (e) {
      toast.error(e?.message || 'Sıralama güncellenemedi');
      await loadGallery();
    }
  };

  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerZoom(1);
    setViewerOpen(true);
  };

  const handleViewerClose = () => {
    setViewerOpen(false);
    setViewerZoom(1);
  };

  const handleViewerPrev = () => {
    setViewerIndex((prev) => (prev <= 0 ? images.length - 1 : prev - 1));
    setViewerZoom(1);
  };

  const handleViewerNext = () => {
    setViewerIndex((prev) => (prev >= images.length - 1 ? 0 : prev + 1));
    setViewerZoom(1);
  };

  const handleZoomIn = () => {
    setViewerZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setViewerZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const SortableImageItem = ({ img, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: img.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: uploading ? 'default' : 'grab',
    };
    const imageUrl = resolveGalleryAssetUrl(img.thumbnailUrl || img.url);

    return (
      <Grid
        item
        xs={6}
        sm={4}
        md={3}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(uploading || editSaving ? {} : listeners)}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1,
            border: '1px solid #e7e7e7',
            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={img.altText || 'galeri-gorsel'}
            sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }}
            onClick={() => openViewer(index)}
          />
          <Typography sx={{ fontSize: 12, color: '#a3a3a3', mt: 0.5 }} noWrap>
            {img.altText || '-'}
          </Typography>
          <TooltipKaldir
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveImage(img.id);
            }}
          />
        </Paper>
      </Grid>
    );
  };

  const TooltipKaldir = ({ onClick }) => (
    <Tooltip title="Kaldır">
      <IconButton
        size="small"
        onClick={onClick}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          bgcolor: 'rgba(255,255,255,0.95)',
          borderRadius: '6px',
          width: 32,
          height: 32,
          '&:hover': { bgcolor: '#fff' },
          '& svg': { fontSize: '16px', color: '#F8285A' },
        }}
        disabled={uploading}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (loading) {
    return (
      <Box>
        <BackButton />
        <Card sx={{ mt: 2, ...cardSx }}>
          <CardContent sx={{ py: 3, px: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 24, width: 200, bgcolor: '#f0f0f0', borderRadius: 1 }} />
                  <Box sx={{ height: 14, width: 120, bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }} />
                </Box>
                <Box sx={{ height: 36, width: 88, bgcolor: '#f0f0f0', borderRadius: '5px' }} />
              </Box>
              <Stack spacing={1.5}>
                <Box sx={{ height: 40, bgcolor: '#fafafa', borderRadius: '5px' }} />
                <Box sx={{ height: 72, bgcolor: '#fafafa', borderRadius: '5px' }} />
                <Box sx={{ height: 40, bgcolor: '#fafafa', borderRadius: '5px' }} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!gallery) {
    return (
      <Box>
        <BackButton />
        <Paper elevation={0} sx={{ ...imagesPanelSx, mt: 2, py: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Galeri bulunamadı.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <BackButton />
      <Card sx={{ mt: 2, mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#444444' }}>
                {editForm.title || gallery.title || 'Galeri Detayı'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#a3a3a3', fontWeight: 500 }}>
                Tip: {galleryEntityTypeLabel(editForm.relatedEntityType)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }} justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
              <Typography sx={{ fontSize: 13, color: '#a3a3a3', fontWeight: 500 }}>Toplam: {images.length}/50</Typography>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={uploading || editSaving}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  backgroundColor: '#0E4D94',
                  borderRadius: '5px',
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#0a3d7a', boxShadow: 'none' },
                }}
              >
                {editSaving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {editError && (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                {editError}
              </Alert>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Başlık</Typography>
              <TextField
                value={editForm.title}
                onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                size="small"
                fullWidth
                disabled={editSaving}
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Açıklama</Typography>
              <TextField
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={editSaving}
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Galeri Tipi</Typography>
              <TextField
                select
                value={editForm.relatedEntityType}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setEditForm((prev) => ({ ...prev, relatedEntityType: val, relatedEntityId: null }));
                  setSelectedEntity(null);
                  setEntityQuery('');
                  setEntityOptions([]);
                  setLinkedEntityName('');
                }}
                size="small"
                fullWidth
                disabled={editSaving}
                sx={textFieldSx}
              >
                {GALLERY_ENTITY_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.showOnDetailPage}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, showOnDetailPage: e.target.checked }))}
                    disabled={editSaving}
                  />
                }
                label="Detay sayfasında göster"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editForm.showInGalleryPool}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, showInGalleryPool: e.target.checked }))}
                    disabled={editSaving}
                  />
                }
                label="Galeri havuzunda göster"
              />
            </Stack>

            {isEntityRequired && (
              <Autocomplete
                options={entityOptions}
                getOptionLabel={(o) => o?.label || ''}
                value={selectedEntity}
                onChange={(_, v) => {
                  setSelectedEntity(v);
                  setLinkedEntityName(v?.label || '');
                  setEditForm((prev) => ({ ...prev, relatedEntityId: v?.id || null }));
                }}
                onInputChange={(_, v) => setEntityQuery(v)}
                loading={entityLoading}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={editForm.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting ? 'Toplantı Seç' : 'Etkinlik Seç'}
                    placeholder="İsme göre ara..."
                    size="small"
                    required
                    disabled={editSaving}
                    sx={textFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {entityLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {isEntityRequired && editForm.relatedEntityId && (
              <Typography sx={{ fontSize: 13, color: '#666' }}>
                Bağlı kayıt:{' '}
                {linkedEntityPath ? (
                  <Link component={RouterLink} to={linkedEntityPath} underline="hover" sx={{ color: '#0E4D94', fontWeight: 500 }}>
                    {linkedEntityName || selectedEntity?.label || `ID: ${editForm.relatedEntityId || editForm.relatedEntityStringId}`}
                  </Link>
                ) : (
                  linkedEntityName || selectedEntity?.label || `ID: ${editForm.relatedEntityId || editForm.relatedEntityStringId}`
                )}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, ...cardSx }}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Alt Metin (opsiyonel)</Typography>
              <TextField
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                size="small"
                fullWidth
                disabled={uploading}
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={uploading || images.length >= 50}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-end' },
                mt: { xs: 0, sm: 2.5 },
                textTransform: 'none',
                fontSize: '13px',
                borderRadius: '5px',
                boxShadow: 'none',
                borderColor: '#e6e6e6',
                color: '#444444',
                px: 2,
                py: 1.25,
                '&:hover': { borderColor: '#0E4D94', backgroundColor: '#E7EDF4' },
              }}
            >
              {uploading ? <CircularProgress size={18} /> : 'Görsel Ekle'}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={(e) => {
                  handleAddImages(e.target.files);
                  e.target.value = '';
                }}
              />
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {images.length === 0 ? (
        <Paper elevation={0} sx={imagesPanelSx}>
          <Typography sx={{ textAlign: 'center', fontSize: '14px', color: '#a3a3a3', py: 2 }}>
            Henüz görsel yok.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={imagesPanelSx}>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={images.map((x) => x.id)} strategy={rectSortingStrategy}>
              <Grid container spacing={2}>
                {images.map((img, index) => (
                  <SortableImageItem key={img.id} img={img} index={index} />
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        </Paper>
      )}

      <Dialog open={viewerOpen} onClose={handleViewerClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 2,
            py: 2,
            borderBottom: '1px solid #e7e7e7',
          }}
        >
          Görsel Önizleme
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={handleZoomOut} disabled={viewerZoom <= 0.5}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleZoomIn} disabled={viewerZoom >= 3}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleViewerClose}
              sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Typography sx={{ fontSize: 13, color: '#a3a3a3', mb: 2 }}>
            {images.length ? `${viewerIndex + 1} / ${images.length}` : ''}
          </Typography>

          {images.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={handleViewerPrev} sx={{ color: '#0E4D94' }}>
                <NavigateBeforeIcon />
              </IconButton>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 420,
                  maxHeight: '70vh',
                  border: '1px solid #e7e7e7',
                  borderRadius: '8px',
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  component="img"
                  src={resolveGalleryAssetUrl(images[viewerIndex]?.thumbnailUrl || images[viewerIndex]?.url)}
                  alt={images[viewerIndex]?.altText || `image-${viewerIndex}`}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    transform: `scale(${viewerZoom})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <IconButton onClick={handleViewerNext} sx={{ color: '#0E4D94' }}>
                <NavigateNextIcon />
              </IconButton>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GalleriesDetail;

